import React from 'react';
import { connect } from 'react-redux';
import { PropTypes } from 'prop-types';
import { Link } from 'react-router-dom';
import withStyles from '@mui/styles/withStyles';
import { Grid, Stack, Box, Typography, Icon, Avatar } from '@mui/material';
import { withIsMobile, withRouter } from 'utils';
import { LoadingView } from 'components';
import { StoreUtil } from 'doctivity-shared/utils';
import {
  loadFavoriteLocations,
  loadLocationActivityNotes,
  loadLocationConnections,
} from 'store/actions/dashboardActions';
import ListItemDetail, {
  ListItem,
  ListItemActionButton,
  ListItemLink,
  DetailContainer,
} from '../../components/ListItemDetail';
import AnalyticsCard from '../../components/AnalyticsCard';
import { ProviderCommentsDialog } from 'components';
import { upsertProviderComment } from 'store/actions/providerCommentsActions';
import { showConfirmationDialog } from 'store/actions/systemActions';

const styles = (theme) => ({
  locationName: {
    display: 'block',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    color: '#333333',
    fontSize: '16px',
    [theme.breakpoints.down('md')]: {
      fontSize: '14px',
    },
  },
  favoriteIcon: {
    fontSize: '21px',
    verticalAlign: 'text-top',
    color: '#E300B8',
    marginRight: '10px',
  },
  addressContainer: {},
  addressText: {
    fontSize: '14px',
    color: '#333',
    fontWeight: 300,
    [theme.breakpoints.down('md')]: {
      fontSize: '12px',
    },
  },

  activityNotesIcon: {
    fontSize: '70px',
    color: '#02E39D',
    marginTop: '5px',
  },
});

class DashboardLocationsPage extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      selectedLocation: 0,
      newNoteDialogOpen: false,
    };
  }

  componentDidMount() {
    if (StoreUtil.needsLoadNoCache(this.props.favoriteLocations)) {
      this.fetchData();
    }
  }

  componentDidUpdate(prevProps) {
    if (
      this.props.app.selectedClient !== prevProps.app.selectedClient ||
      StoreUtil.needsLoadNoCache(this.props.favoriteLocations)
    ) {
      this.fetchData();
    }

    if (
      !StoreUtil.isLoaded(prevProps.favoriteLocations) &&
      StoreUtil.isLoaded(this.props.favoriteLocations)
    ) {
      this.fetchSelected();
    }
  }

  render() {
    const { classes, dashboard, favoriteLocations, isMobile, isEarlyMobile } =
      this.props;

    if (
      !dashboard ||
      !favoriteLocations ||
      !StoreUtil.isLoaded(favoriteLocations)
    ) {
      return <LoadingView />;
    }

    if (favoriteLocations.data.length === 0) {
      return (
        <Box textAlign='center' padding='25px'>
          <Typography>No favorite locations</Typography>
        </Box>
      );
    }

    return (
      <>
        <Grid
          container
          sx={{
            px: 3,
            py: 2,
          }}
        >
          <ListItemDetail
            selectedItemIndex={this.state.selectedLocation}
            items={StoreUtil.getData(favoriteLocations)}
            renderItem={({ item: favorite, index, renderDetail }) => {
              return (
                <Stack key={favorite.location_id}>
                  <ListItem
                    isSelected={this.state.selectedLocation === index}
                    onClick={() =>
                      this.selectLocation(favorite.location_id, index)}
                  >
                    <Grid
                      container
                      columnSpacing={1}
                      alignItems='center'
                      sx={{
                        height: '100%',
                      }}
                    >
                      <Grid item xs={6} sm={6} xl={5}>
                        <Box
                          sx={{
                            paddingLeft: 1,
                          }}
                        >
                          <Typography
                            component='span'
                            sx={{ display: 'block', float: 'left' }}
                          >
                            <Icon className={classes.favoriteIcon}>
                              favorite
                            </Icon>
                          </Typography>
                          <Typography
                            component='span'
                            className={classes.locationName}
                          >
                            {`${favorite.name ? favorite.name : ''}`}
                          </Typography>
                        </Box>
                      </Grid>
                      <Box
                        component={Grid}
                        item
                        xs={3}
                        sm={3}
                        xl={4}
                        className={classes.addressContainer}
                      >
                        <Typography className={classes.addressText}>
                          {!isMobile
                            ? `${favorite.address1}${
                                favorite.address2
                                  ? ', ' + favorite.address2
                                  : ''
                              }, ${favorite.city}, ${favorite.state}`
                            : `${favorite.city}, ${favorite.state}`}
                        </Typography>
                      </Box>
                      <Grid item xs={3} sm={3} xl={3}>
                        <Box
                          sx={{
                            paddingRight: 1,
                            textAlign: 'right',
                          }}
                        >
                          {!isMobile &&
                            this.state.selectedLocation === index && (
                              <ListItemLink
                                component={Link}
                                to={'/locations/' + favorite.location_id}
                              >
                                View
                              </ListItemLink>
                            )}
                          <ListItemActionButton
                            onClick={() =>
                              this.openNewNoteDialog(favorite.location_id)}
                          >
                            + New Note
                          </ListItemActionButton>
                        </Box>
                      </Grid>
                    </Grid>
                  </ListItem>
                  {this.state.selectedLocation === index &&
                    (isMobile || isEarlyMobile) &&
                    renderDetail()}
                </Stack>
              );
            }}
            renderDetail={this.renderSelectedLocationDetails}
          />
          <Link to='/favorite_locations'>See all favorite locations</Link>
        </Grid>
        {this.state.newNoteDialogOpen && (
          <ProviderCommentsDialog
            clientId={this.props.app.selectedClient}
            open={this.state.newNoteDialogOpen}
            onSave={this.onSaveNewNote}
            onClose={this.closeNewNoteDialog}
          />
        )}
      </>
    );
  }

  renderSelectedLocationDetails = () => {
    const { classes, dashboard, favoriteLocations, clientId } = this.props;
    const { selectedLocation } = this.state;

    if (!StoreUtil.isLoaded(favoriteLocations)) {
      return <LoadingView />;
    }

    let selectedFavoriteLocation = favoriteLocations.data[selectedLocation];

    const totalActivityNotes =
      dashboard[
        `analytics-location-activitynotes-${clientId}-${selectedFavoriteLocation.location_id}`
      ];
    let loadedTotalActivityNotes = false;
    let totalActivityNotesCount = 0;
    if (StoreUtil.isLoaded(totalActivityNotes)) {
      loadedTotalActivityNotes = true;

      totalActivityNotesCount = totalActivityNotes.data.total;
    }

    const connections =
      dashboard[
        `analytics-location-connections-${clientId}-${selectedFavoriteLocation.location_id}`
      ];
    let loadedConnections = false;
    let totalProviderConnections = 0;
    let totalContactConnections = 0;
    if (StoreUtil.isLoaded(connections)) {
      loadedConnections = true;
      totalContactConnections = connections.data.contacts_total;
      totalProviderConnections = connections.data.providers_total;
    }

    return (
      <DetailContainer>
        <Grid item xs={6}>
          <AnalyticsCard>
            {!loadedTotalActivityNotes ? (
              <LoadingView />
            ) : (
              <>
                <Box>
                  <Typography
                    fontSize='18px'
                    color='#333333'
                    textAlign='center'
                    marginTop='10px'
                  >
                    Total Activity Notes
                  </Typography>
                </Box>
                <Box marginTop='25%'>
                  <Grid
                    container
                    alignItems='center'
                    justifyContent='center'
                    spacing={1}
                  >
                    <Grid item>
                      <Icon className={classes.activityNotesIcon}>
                        speaker_notes
                      </Icon>
                    </Grid>
                    <Grid item>
                      <Typography fontSize='64px'>
                        {totalActivityNotesCount}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              </>
            )}
          </AnalyticsCard>
        </Grid>
        <Grid item xs={6}>
          <AnalyticsCard>
            {!loadedConnections ? (
              <LoadingView />
            ) : (
              <Box>
                <Box>
                  <Typography
                    fontSize='18px'
                    color='#333333'
                    textAlign='center'
                    marginTop='10px'
                  >
                    Connections
                  </Typography>
                </Box>
                <Box padding='15px 25px'>
                  <Avatar
                    sx={{
                      bgcolor: '#FF6B6B',
                      width: '170px',
                      height: '170px',
                      boxShadow: '0px 2px 4px #00000029',
                    }}
                  >
                    <Box textAlign='center'>
                      <Typography
                        fontSize='45px'
                        fontWeight='500'
                        lineHeight='1.2'
                      >
                        {totalProviderConnections}
                      </Typography>
                      <Typography fontSize='18px' fontWeight='300'>
                        Providers <br />
                        Connected
                      </Typography>
                    </Box>
                  </Avatar>
                  <Avatar
                    sx={{
                      bgcolor: '#FFBF69',
                      width: '140px',
                      height: '140px',
                      boxShadow: '0px 2px 4px #00000029',
                      float: 'right',
                      top: '-15px',
                    }}
                  >
                    <Box textAlign='center'>
                      <Typography
                        fontSize='45px'
                        fontWeight='500'
                        lineHeight='1.2'
                      >
                        {totalContactConnections}
                      </Typography>
                      <Typography fontSize='18px' fontWeight='300'>
                        Contacts <br />
                        Connected
                      </Typography>
                    </Box>
                  </Avatar>
                </Box>
              </Box>
            )}
          </AnalyticsCard>
        </Grid>
      </DetailContainer>
    );
  };

  fetchData() {
    const { favoriteLocations } = this.props;

    if (!StoreUtil.isLoaded(favoriteLocations)) {
      this.props.dispatch(
        loadFavoriteLocations({
          clientId: this.props.clientId,
        })
      );
    }
  }

  fetchSelected() {
    const { dispatch, favoriteLocations, clientId } = this.props;
    let selectedFavoriteLocation =
      favoriteLocations.data[this.state.selectedLocation];
    if (selectedFavoriteLocation) {
      dispatch(
        loadLocationActivityNotes({
          locationId: selectedFavoriteLocation.location_id,
          clientId,
        })
      );

      dispatch(
        loadLocationConnections({
          locationId: selectedFavoriteLocation.location_id,
          clientId,
        })
      );
    }
  }

  selectLocation = (locationId, index) => {
    this.setState(
      {
        selectedLocation: index,
      },
      () => {
        this.fetchSelected();
      }
    );
  };

  onSaveNewNote = (data) => {
    const { app, user, dispatch } = this.props;
    data.location_id = this.state.newNoteForLocationId;
    if (
      data.body &&
      data.body.length > 0 &&
      (data.providers.length > 0 ||
        data.contacts.length > 0 ||
        data.location_id)
    ) {
      data.client_id = app.selectedClient;
      data.user_id = user.id;

      dispatch(upsertProviderComment(data));
    }
    this.closeNewNoteDialog();
  };

  closeNewNoteDialog = () => {
    this.setState({
      newNoteDialogOpen: false,
      newNoteForLocationId: -1,
    });
  };

  openNewNoteDialog = (locationId) => {
    if (this.props.user.is_admin) {
      this.props.dispatch(
        showConfirmationDialog({
          title: 'Admin Users Can Not Create Notes',
          content: 'Login as a non-admin user to create activity notes.',
          confirmLabel: 'OK',
          onConfirm: () => {
            // do nothing
          },
        })
      );
    } else {
      this.setState({
        newNoteDialogOpen: true,
        newNoteForLocationId: locationId,
      });
    }
  };
}

DashboardLocationsPage.propTypes = {
  isMobile: PropTypes.bool.isRequired,
  isEarlyMobile: PropTypes.bool.isRequired,
  classes: PropTypes.object.isRequired,
  app: PropTypes.object,
  user: PropTypes.object,
  router: PropTypes.object.isRequired,
  clientId: PropTypes.number.isRequired,
  dashboard: PropTypes.object,
  dispatch: PropTypes.func.isRequired,
  userId: PropTypes.number,
  favoriteLocations: PropTypes.object,
};

function mapStateToProps(state) {
  const { app, user, dashboard } = state;
  const userId = state.user ? state.user.id : null;
  const clientId = state.app.selectedClient;

  return {
    app,
    user,
    userId,
    clientId,
    dashboard,
    favoriteLocations: StoreUtil.get(
      dashboard,
      'favorite-locations-' + clientId
    ),
  };
}

const withStyled = withStyles(styles)(withIsMobile(DashboardLocationsPage));
const connected = connect(mapStateToProps)(withStyled);
const routed = withRouter(connected);
export { routed as DashboardLocationsPage };
